import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminStats } from '@shared/types';
import { api, ApiError } from '../lib/api';

const DAILY_COLUMNS = [
  { name: 'share_link_opened', label: 'Opens' },
  { name: 'trip_created', label: 'Trips' },
  { name: 'trip_saved', label: 'Saves' },
  { name: 'signup_from_share', label: 'Signups' },
  { name: 'chat_turn', label: 'Chat turns' },
] as const;

function fmtDay(sec: number): string {
  return new Date(sec * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function AdminPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api
      .adminStats()
      .then(setStats)
      .catch((err) => {
        if (err instanceof ApiError) setDenied(true);
      });
  }, []);

  if (denied) {
    return (
      <main className="mx-auto max-w-lg px-5 py-24 text-center">
        <h1 className="font-display text-title text-ink">No trip here</h1>
        <Link to="/" className="btn-quiet mt-6 inline-block">
          Go to your trips
        </Link>
      </main>
    );
  }

  if (!stats) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-16">
        <p className="font-util text-meta text-muted">Loading…</p>
      </main>
    );
  }

  // Pivot the daily rows into one row per date.
  const byDate = new Map<string, Record<string, number>>();
  for (const row of stats.daily) {
    const entry = byDate.get(row.date) ?? {};
    entry[row.name] = row.count;
    byDate.set(row.date, entry);
  }
  const dates = [...byDate.keys()].sort().reverse();

  return (
    <main className="mx-auto max-w-2xl px-5 py-10">
      <div className="grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
        <div>
          <p className="font-display text-[2rem] leading-none text-ink">{stats.users.named}</p>
          <p className="mt-1.5 font-util text-meta text-muted">
            accounts · {stats.users.namedLast7Days} this week
          </p>
        </div>
        <div>
          <p className="font-display text-[2rem] leading-none text-ink">{stats.trips.total}</p>
          <p className="mt-1.5 font-util text-meta text-muted">
            trips · {stats.trips.last7Days} this week
          </p>
        </div>
        <div>
          <p className="font-display text-[2rem] leading-none text-ink">
            {stats.shareLinks.totalOpens}
          </p>
          <p className="mt-1.5 font-util text-meta text-muted">
            link opens · {stats.shareLinks.total - stats.shareLinks.revoked} live links
          </p>
        </div>
        <div>
          <p className="font-display text-[2rem] leading-none text-ink">{stats.wishlist.saves}</p>
          <p className="mt-1.5 font-util text-meta text-muted">wishlist saves</p>
        </div>
      </div>

      <p className="mt-6 font-util text-meta text-muted">
        {stats.users.guests} visitors browsed without an account. {stats.items.total} places saved
        across all trips; {stats.trips.withItems} of {stats.trips.total} trips have at least one.
        {(stats.eventTotals.signup_from_share ?? 0) > 0 &&
          ` ${stats.eventTotals.signup_from_share} signups came from a shared link.`}
      </p>

      <section className="mt-10">
        <h2 className="font-display text-[1.125rem] text-ink">Last 14 days</h2>
        {dates.length === 0 ? (
          <p className="mt-2 font-util text-meta text-muted">Nothing yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full font-util text-meta">
              <thead>
                <tr className="border-b border-ink/8 text-left text-muted">
                  <th className="py-1.5 pr-4 font-medium">Day</th>
                  {DAILY_COLUMNS.map((col) => (
                    <th key={col.name} className="py-1.5 pr-4 text-right font-medium">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dates.map((date) => (
                  <tr key={date} className="border-b border-ink/8">
                    <td className="py-1.5 pr-4 text-ink">{date.slice(5)}</td>
                    {DAILY_COLUMNS.map((col) => (
                      <td key={col.name} className="py-1.5 pr-4 text-right text-ink">
                        {byDate.get(date)?.[col.name] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-display text-[1.125rem] text-ink">Latest trips</h2>
        <ul className="mt-3">
          {stats.recentTrips.map((trip) => (
            <li
              key={trip.id}
              className="flex items-baseline justify-between gap-4 border-b border-ink/8 py-2"
            >
              <span className="min-w-0">
                <Link to={`/t/${trip.slug}`} className="text-ink underline-offset-2 hover:underline">
                  {trip.title}
                </Link>
                <span className="ml-2 font-util text-meta text-muted">{trip.ownerEmail}</span>
              </span>
              <span className="shrink-0 font-util text-meta text-muted">
                {trip.itemCount} places · {fmtDay(trip.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-[1.125rem] text-ink">Latest signups</h2>
        <ul className="mt-3">
          {stats.recentSignups.map((person) => (
            <li
              key={person.email}
              className="flex items-baseline justify-between gap-4 border-b border-ink/8 py-2"
            >
              <span className="text-ink">
                {person.name ?? person.email}
                {person.name && (
                  <span className="ml-2 font-util text-meta text-muted">{person.email}</span>
                )}
              </span>
              <span className="shrink-0 font-util text-meta text-muted">
                {fmtDay(person.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
